// Splitting live content into screen-sized slides: song lyrics by line (or
// groups of lines), long scripture passages by sentence. Everything that shows
// the live content (projector, stage, OBS, phone, console) uses the same
// slides so they always agree.

export const DEFAULT_LYRICS_PER_SLIDE = 1;
export const DEFAULT_SCRIPTURE_SLIDE_CHARS = 320;

// 0 = never split a passage.
export const SCRIPTURE_SLIDE_SIZES = [
  { value: 0, label: 'Whole passage on one slide' },
  { value: 200, label: 'Small slides (about 200 characters)' },
  { value: 320, label: 'Medium slides (about 320 characters)' },
  { value: 480, label: 'Large slides (about 480 characters)' },
];

export const LYRICS_PER_SLIDE_OPTIONS = [1, 2, 3, 4];

const nonEmptyLines = (text) => String(text || '').split('\n').map(l => l.replace(/\s+$/, '')).filter(l => l.trim() !== '');

/** Lyrics as slides of `perSlide` lines, never mixing two stanzas (blank-line separated) in one slide. */
export function songSlides(text, perSlide = DEFAULT_LYRICS_PER_SLIDE) {
  const size = Math.max(1, Math.floor(Number(perSlide)) || 1);
  if (size === 1) return nonEmptyLines(text);
  const slides = [];
  String(text || '').split(/\n\s*\n/).forEach((stanza) => {
    const lines = nonEmptyLines(stanza);
    for (let i = 0; i < lines.length; i += size) slides.push(lines.slice(i, i + size).join('\n'));
  });
  return slides;
}

// Multi-verse text from the Bible service looks like "16 For God ... 17 For God ...".
// Split before each verse number (the next one in sequence, so ordinary numbers in a
// verse are never mistaken for one).
function verseParts(text) {
  const words = text.split(' ');
  let expected = /^\d{1,3}$/.test(words[0]) ? parseInt(words[0], 10) : null;
  const parts = [];
  let current = [];
  words.forEach((word, i) => {
    if (expected !== null && i > 0 && word === String(expected + 1)) {
      parts.push(current.join(' '));
      current = [word];
      expected += 1;
    } else {
      current.push(word);
    }
  });
  parts.push(current.join(' '));
  return parts;
}

// Pieces to pack into slides: each verse, cut further at sentence/clause ends.
function pieces(text) {
  const out = [];
  verseParts(text).forEach((verse) => {
    const sentences = verse.match(/[^.!?;:]+[.!?;:]*["”’')\]]*\s*/g) || [verse];
    sentences.forEach(sentence => { if (sentence.trim()) out.push(sentence.trim()); });
  });
  return out;
}

// A single piece longer than the limit is cut at word boundaries.
function cutWords(piece, max) {
  const out = [];
  let current = '';
  piece.split(/\s+/).forEach((word) => {
    if (current && (current + ' ' + word).length > max) { out.push(current); current = word; }
    else current = current ? `${current} ${word}` : word;
  });
  if (current) out.push(current);
  return out;
}

/** A passage as slides of at most about `maxChars` characters (0 or less = one slide). */
export function scriptureSlides(text, maxChars = DEFAULT_SCRIPTURE_SLIDE_CHARS) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  if (!(maxChars > 0) || clean.length <= maxChars) return [clean];
  const slides = [];
  let current = '';
  pieces(clean).forEach((piece) => {
    cutWords(piece, maxChars).forEach((part) => {
      if (current && (current + ' ' + part).length > maxChars) { slides.push(current); current = part; }
      else current = current ? `${current} ${part}` : part;
    });
  });
  if (current) slides.push(current);
  return slides;
}

/** The slides for whatever is on air (scripture, custom text or a song); [] for nothing. */
export function contentSlides(content, settings = {}) {
  if (!content) return [];
  if (content.type === 'scripture') {
    const max = settings.scriptureSlideChars === undefined ? DEFAULT_SCRIPTURE_SLIDE_CHARS : settings.scriptureSlideChars;
    return scriptureSlides(content.content, max);
  }
  if (content.type === 'song') {
    return songSlides(content.content, settings.lyricsPerSlide === undefined ? DEFAULT_LYRICS_PER_SLIDE : settings.lyricsPerSlide);
  }
  return [];
}

/**
 * The text of slide `index`. `slides` is what the console sent; when it is not an
 * array (an older console) fall back to the whole passage / one lyric line.
 */
export function slideText(content, slides, index) {
  if (!content) return '';
  const list = Array.isArray(slides)
    ? slides
    : (content.type === 'song' ? songSlides(content.content, 1) : content.type === 'scripture' ? [content.content || ''] : []);
  if (list.length === 0) return '';
  return list[Math.min(Math.max(0, index || 0), list.length - 1)] || '';
}

/** The slide after `index`, or '' at the end. */
export function nextSlideText(content, slides, index) {
  const list = Array.isArray(slides) ? slides : (content && content.type === 'song' ? songSlides(content.content, 1) : []);
  return list[(index || 0) + 1] || '';
}
